import { styled } from 'styled-components';
import { COLORS, computeRemSize, STYLE } from '@ydistri/theme';

/** How tall the dock may actually get, whatever height is stored. */
export const YBOT_DOCK_VIEWPORT_CAP = `calc(100vh - ${STYLE.TOP_MENU_HEIGHT})`;

/**
 * Overlays the main column; deliberately no part in page layout. Reserving space with padding/flex
 * shrank the screens above (the calculation table keeps its measured height and pokes out - the
 * second scrollbar this replaced) and the padding dragged the dock around on scroll. Absolute
 * against the never-scrolling column keeps it clear of the left menu.
 *
 * The height arrives as an inline style: a styled prop would mint a class per drag frame.
 */
export const DockPanel = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  /* Above the content overlay's 999: the dock is chrome, like the left menu, and a table
     reloading behind it is no reason to grey out a conversation being typed. Modals still win -
     antd gives them the same 1000 but renders them into a portal later in the document. */
  z-index: 1000;
  display: flex;
  flex-direction: column;
  max-height: ${YBOT_DOCK_VIEWPORT_CAP};
  background: ${COLORS.BACKGROUND};
  border-top: 1px solid ${COLORS.BORDER};
  box-shadow: ${STYLE.BASE_SHADOW};
`;

export const DockResizeHandle = styled.div`
  height: ${computeRemSize(6)};
  flex: 0 0 auto;
  cursor: row-resize;
  /* Without this a touch drag scrolls the page instead. */
  touch-action: none;
  background: ${COLORS.GREY_LIGHT};
  border-bottom: 1px solid ${COLORS.GREY_BORDER};

  &:hover {
    background: ${COLORS.HOVER_GREY_LIGHT};
  }
`;

export const DockHeader = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.25rem 0.5rem;
  border-bottom: 1px solid ${COLORS.GREY_BORDER};
`;

export const DockHeaderGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const DockBody = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
`;

export const DockConversations = styled.div`
  flex: 0 0 20%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  border-right: 1px solid ${COLORS.GREY_BORDER};
  ${STYLE.SCROLLBAR}
`;

export const DockMain = styled.div`
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
`;

/**
 * Deliberately not the scroll container: `Bubble.List` scrolls itself and follows a growing answer
 * only when it is the element with the bounded height.
 */
export const DockTranscript = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  padding: 0.5rem;

  & > * {
    flex: 1 1 auto;
    min-height: 0;
    ${STYLE.SCROLLBAR}
  }
`;

export const DockPromptRow = styled.div`
  flex: 0 0 auto;
  padding: 0.5rem;
  border-top: 1px solid ${COLORS.GREY_BORDER};
`;

/** In the header, not the transcript: current state, not part of the record. */
export const DockToolChips = styled.div`
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
  overflow: hidden;
`;

export const DockToolChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex: 0 1 auto;
  min-width: 0;
  padding: 0.0625rem 0.375rem;
  border: 1px solid ${COLORS.BORDER};
  border-radius: 999px;
  background: ${COLORS.GREY_LIGHT};
  font-size: ${computeRemSize(11)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Pulses while the call is open. */
export const DockToolDot = styled.span`
  flex: 0 0 auto;
  width: ${computeRemSize(6)};
  height: ${computeRemSize(6)};
  border-radius: 50%;
  background: ${COLORS.PRIMARY};

  @keyframes ybot-tool-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.25;
    }
  }

  animation: ybot-tool-pulse 1.2s ease-in-out infinite;
`;

export const DockConversationRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  min-width: 0;
`;

/** Separate from the row so the menu button beside it does not inherit the tooltip. */
export const DockConversationLabel = styled.div`
  display: flex;
  flex: 1;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  min-width: 0;
`;

export const DockConversationTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Chat-sized margins for Markdown blocks - the browser's defaults are sized for documents. */
export const DockMarkdown = styled.div`
  & > *:first-child {
    margin-top: 0;
  }

  & > *:last-child {
    margin-bottom: 0;
  }

  p,
  ul,
  ol,
  pre,
  table,
  blockquote {
    margin: 0 0 0.5rem;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0.75rem 0 0.25rem;
    font-size: 1em;
    font-weight: 600;
  }

  ul,
  ol {
    padding-left: 1.25rem;
  }

  li + li {
    margin-top: 0.125rem;
  }

  code {
    padding: 0.0625rem 0.25rem;
    border-radius: ${computeRemSize(3)};
    background: ${COLORS.GREY_MEDIUM2};
    font-size: 0.9em;
  }

  pre {
    padding: 0.5rem;
    border-radius: ${computeRemSize(STYLE.BORDER_RADIUS)};
    background: ${COLORS.GREY_LIGHT};
    overflow-x: auto;
    ${STYLE.SCROLLBAR}

    code {
      padding: 0;
      background: none;
    }
  }

  table {
    border-collapse: collapse;
    /* Its own scroller: a wide table must not stretch the bubble. */
    display: block;
    overflow-x: auto;
    max-width: 100%;
  }

  th,
  td {
    padding: 0.125rem 0.375rem;
    border: 1px solid ${COLORS.GREY_BORDER};
    text-align: left;
  }

  blockquote {
    padding-left: 0.5rem;
    border-left: ${computeRemSize(2)} solid ${COLORS.GREY_BORDER};
    color: ${COLORS.GREY};
  }

  a {
    overflow-wrap: anywhere;
  }
`;

/**
 * The "sent from" marker on a user message. Almost invisible at rest and legible on hover or focus:
 * the answer is what the reader came for, and this is only for the moment they wonder what the bot
 * was looking at. Its own opacity rather than the bubble's `:hover`, which would tie the dock to
 * antd's internal class names.
 *
 * Not zero at rest, deliberately: a marker nobody can see is a marker nobody thinks to hover. See
 * it is a flagged deviation from "on hover only".
 */
export const DockContextTrigger = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: ${COLORS.GREY};
  font-size: ${computeRemSize(13)};
  line-height: 1;
  cursor: help;
  opacity: 0.3;
  transition: opacity 0.15s ease-in-out;

  &:hover,
  &:focus-visible {
    opacity: 1;
  }
`;
